import {PurchaseOrderItem} from "../../../api/Logistics";
import {Delivery, DeliveryItem} from "../../../api/Deliveries";
import { groupByArr, orderByStrAscending} from "nate-react-api-helpers";
import {forwardRef, useContext, useMemo} from "react";
import {Page, pdfTableStyle} from "../../../misc/pdf/parts/Page";
import moment from "moment";
import {DataSplitInfo, PageRenderProvider} from "../../../misc/pdf/parts/PageContext";
import {PDF} from "../../../misc/pdf/parts/PDF";
import {Project, projectLabel} from "../../../api/Projects";
import {PDFHeader} from "../../../misc/pdf/parts/PDFHeader";
import {useReadyCheck} from "../../../misc/pdf/UseReadyCheck";
import {table} from "../../../misc/pdf/parts/TableStyle";
import {formatDescription} from "../purchasing/toSource/SubmitButton";
import {hollowMetalDoor, hollowMetalFrame, woodDoor} from "../../project/quote/openings/Const";
import {DropDownContext, useDropdownData} from "../../project/settings/DropDowns";

export type Row = {
    type: "data"
    qty: number;
    qtyDamagedOrMissing: number;
    description: string;
    openings?: {name: string, strikeThrough?: true}[];
    deliveryBox?: number;
    deliveryBoxName?: string;
} | {
    type: "header"
    title: string;
}

function isDeliveryItem(input: any): input is DeliveryItem {
    if(!input) return false;
    return typeof input === "object" && input.hasOwnProperty("delivery")
}

export function usePickingSlipData(props: {
    items: (DeliveryItem | PurchaseOrderItem)[];
}) {
    const ctx = useContext(DropDownContext);
    const ddlOptions = useDropdownData(ctx.root);

    const rows = useMemo(() => {
        const doorsHm: Row[] = [];
        const doorsWd: Row[] = [];
        const hardware: Row[] = [];
        const framesHm: Row[] = [];
        const framesOther: Row[] = [];
        const div10CornerGuard: Row[] = [];
        const div10Locker: Row[] = [];
        const div10Mailbox: Row[] = [];
        const div10WrAccessory: Row[] = [];
        const div10WrPartition: Row[] = [];
        const glazing: Row[] = [];

        props.items.map(itm => {
            const obj: Row = {
                type: "data",
                qty: itm.qty,
                qtyDamagedOrMissing: ("qtyDamagedOrMissing" in itm ? itm.qtyDamagedOrMissing : 0) || 0,
                description: "<>",
                openings: (itm.openings as any[])?.map(o => ({
                    name: o.name,
                    strikeThrough: o.pickupRouteQty > o.deliverRouteQty ? true : undefined,
                })) || [],
            }

            if(isDeliveryItem(itm) && itm.deliveryBox) {
                obj.deliveryBox = itm.deliveryBox;
                obj.deliveryBoxName = itm.deliveryBoxType + " " + itm.deliveryBoxLabel;
            }

            switch(itm.kind) {
                case "door": {
                    obj.description = itm.description;
                    if(itm.details) {
                        obj.description = formatDescription(itm.details, {
                            ddlOptions: ddlOptions,
                        })

                        if (itm.details.frameHanding) {
                            obj.description += " " + itm.details.frameHanding;
                        }
                    };

                    switch (itm.details?.doorSeries) {
                        case hollowMetalDoor:
                        default:
                            doorsHm.push(obj)
                            break;
                        case woodDoor:
                            doorsWd.push(obj)
                            break;
                    }

                    break;
                }
                case "frame": {
                    obj.description = itm.details ? formatDescription(itm.details, {
                        ddlOptions: ddlOptions,
                    }) + " " + itm.details.frameHanding : itm.description;

                    switch (itm.details?.frameSeries) {
                        case hollowMetalFrame:
                            framesHm.push(obj)
                            break;
                        default:
                            framesOther.push(obj)
                            break;
                    }

                    break;
                }
                case "frame-anchor":
                case "hardware":
                    obj.description = itm.details ? formatDescription(itm.details, {
                        ddlOptions: ddlOptions,
                    }) : itm.description

                    hardware.push(obj)
                    break;
                case "div-10": {
                    obj.description = itm.details ? formatDescription(itm.details, {
                        ddlOptions: ddlOptions,
                    }) : itm.description;

                    switch (itm.details?.div10Category) {
                        case 'corner-guard':
                            div10CornerGuard.push(obj);
                            break;
                        case 'locker':
                            div10Locker.push(obj);
                            break;
                        case 'mailbox':
                            div10Mailbox.push(obj);
                            break;
                        default:
                        case 'wr-accessory':
                            div10WrAccessory.push(obj);
                            break;
                        case 'wr-partition':
                            div10WrPartition.push(obj);
                            break;
                    }

                    break;
                }
                case "glazing":
                    obj.description = itm.description;
                    glazing.push(obj);
                    break;
                default:
                    console.error("invalid type '" + itm.kind + "'", itm)
                    break;
            }
        })

        function simplify(arr: Row[]) {
            const out: Row[] = [];
            arr.map(a => {
                if(a.type !== "data") return;

                const other = out.find(r => r.type === "data" && r.description === a.description && r.deliveryBox === a.deliveryBox)
                if(other && other.type === "data") {
                    other.qty += a.qty;
                    other.qtyDamagedOrMissing += a.qtyDamagedOrMissing;

                    if(other.openings && a.openings) {
                        other.openings.push(...a.openings);
                    }
                } else {
                    out.push(a);
                }
            })

            out.map(r => {
                if(r.type === "header") return;
                if(r.openings) {
                    const names: string[] = [];

                    r.openings = r.openings.filter(v => {
                        if(names.includes(v.name)) return false;

                        names.push(v.name);
                        return true;
                    })
                    orderByStrAscending(r.openings, v => v.name);
                }
            })

            out.sort((a, b) => {
                if(a.type === "header") return -1;
                if(b.type === "header") return 1;

                const aStr = a.openings ? a.openings.map(v =>v.name).join(", ") : "";
                const bStr = b.openings ? b.openings.map(v =>v.name).join(", ") : "";

                return aStr.localeCompare(bStr)
            })

            return out;
        }

        function sectionNameFormat(sectionName: string, val: Row) {
            const suffix = (val.type === "data" ? "- " + (val.deliveryBoxName || "Delivered Loose") : undefined) || ""
            return (sectionName + " " + suffix).trim()
        }

        function splitAndTitleByBox(list: Row[], sectionName: string): Row[] {
            const grouped = groupByArr(list, v => v.type === "data" ? v.deliveryBox : undefined)
            const result: Row[] = [];

            orderByStrAscending(grouped, v => sectionNameFormat(sectionName, v.values[0]));

            grouped.forEach(section => {
                result.push({
                    type: "header",
                    title: sectionNameFormat(sectionName, section.values[0]),
                })

                result.push(...simplify(section.values))
            })

            return result;
        }

        const result: Row[] = [];

        result.push(...splitAndTitleByBox(framesHm, "HM Frames"))
        result.push(...splitAndTitleByBox(framesOther, "Other Frames"))
        result.push(...splitAndTitleByBox(doorsHm, "HM Doors"))
        result.push(...splitAndTitleByBox(doorsWd, "WD Doors"))
        result.push(...splitAndTitleByBox(hardware, "Hardware"))
        result.push(...splitAndTitleByBox(div10WrAccessory, "Div 10 - Accessories"))
        result.push(...splitAndTitleByBox(div10WrPartition, "Div 10 - Partitions"))
        result.push(...splitAndTitleByBox(div10CornerGuard, "Div 10 Corner Guards"))
        result.push(...splitAndTitleByBox(div10Locker, "Div 10 - Lockers"))
        result.push(...splitAndTitleByBox(div10Mailbox, "Div 10 - Mailboxes"))
        result.push(...splitAndTitleByBox(glazing, "Glazing"))

        return result;
    }, [props.items, ddlOptions]);

    return {
        rows,
        loaded: true,
    }
}

export function buildSections(rows: Row[], lastHeader?: string) {
    type Section = {rows: Row[], header: string}
    const rowsByHeader: Section[] = []
    let currentSection: Section | null = null;

    rows.map((r, index) => {
        if(r.type === "header") {
            currentSection = {rows: [], header: r.title}
            rowsByHeader.push(currentSection)
            return;
        }

        if(!currentSection) {
            if(lastHeader) {
                currentSection = {rows: [], header: lastHeader}
                rowsByHeader.push(currentSection)
            } else {
                throw new Error("Missing section header")
            }
        }

        currentSection.rows.push(r);
    });

    return rowsByHeader
}

export const PickingSlipPDF = forwardRef(function PackingSlipPDF(props: {
    value: Delivery;
    items: DeliveryItem[]
    project: Project;
    onReady(): void;
}, ref: any) {

    const {rows, loaded} = usePickingSlipData({
        items: props.items,
    })

    const ready = useReadyCheck(props.onReady)
    ready.require(loaded)

    return (
        <div ref={ref}>
            <PDF>
                <PageRenderProvider data={rows} onReady={ready.section()}>
                    {(info: DataSplitInfo<Row>) => {
                        const lastHeader = rows.slice(0, info.startingDataIndex).findLast((r: Row) => r.type === "header") as Row | undefined;

                        let headerTitle: string | undefined = undefined;
                        if(lastHeader?.type === "header") {
                            headerTitle = lastHeader.title
                        }

                        const rowsByHeader = buildSections(info.rows, headerTitle);

                        return (
                            <Page orientation="portrait">
                                <div style={{flex: 1}}>
                                    {info.pageIndex === 0 && <>
                                        <PDFHeader noAddress title="Picking Slip">
                                        </PDFHeader>
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            fontSize: "0.8rem",
                                            width: "100%"
                                        }}>
                                            <table className={table}>
                                                <tr>
                                                    <td>Date Created</td>
                                                    <td>S.O. No.</td>
                                                </tr>
                                                <tr>
                                                    <td>{moment().format("MM/DD/YYYY")}</td>
                                                    <td>{props.value.id}</td>
                                                </tr>
                                            </table>

                                            <table className={table}>
                                                <tr>
                                                    <td>Pick By Date</td>
                                                    <td>Project</td>
                                                </tr>
                                                <tr>
                                                    <td>{moment(props.value.pickByDate).format("MM/DD/YYYY")}</td>
                                                    <td>{projectLabel(props.project)}</td>
                                                </tr>
                                            </table>
                                        </div>
                                    </>}

                                    {rowsByHeader.map(section => <>
                                        <div style={{height: 16}}/>
                                        <table className={pdfTableStyle}>
                                            <thead>
                                                <tr>
                                                    <th style={{textAlign: "left"}} colSpan={4}>{section.header}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                            {section.rows.map((i, index) => {
                                                if (i.type === "header") {
                                                    return null;
                                                }

                                                const qtyWidth = 60;

                                                if(!i.openings) {
                                                    return (
                                                        <tr key={index.toString()}>
                                                            <td style={{textAlign: "right", width: qtyWidth}}>{i.qty}</td>
                                                            <td colSpan={3}>{i.description}</td>
                                                        </tr>
                                                    )
                                                }

                                                return (
                                                    <tr key={index.toString()}>
                                                        <td style={{textAlign: "right", width: qtyWidth}}>{i.qty}</td>
                                                        <td>{i.description}</td>
                                                        <td>{i.openings.map(o => o.name).join(", ")}</td>
                                                        <td style={{width: 50}}></td>
                                                    </tr>
                                                )
                                            })}
                                            </tbody>
                                        </table>
                                    </>)}
                                </div>
                            </Page>
                        )}}
                </PageRenderProvider>
            </PDF>
        </div>
    )
});
